import { AlertType, AloftPrice, isDefined } from '@aloft/toolkit-shared';
import { Block, Flex, Icon, SimpleInput, Typography, theme } from '@aloft/toolkit-sui';

import { useMemo } from 'react';

import { ContentBlock } from '../../../components/Analysis/ColumnComponents/ContentBlock.tsx';
import {
  type SummaryDataDisplay,
  selectSummaryDataDisplayById,
} from '../../../redux/features/selectors/costApproachSelectors/selectSubjectSummaryData.ts';
import { selectNumericSiteArea } from '../../../redux/features/selectors/propertyDataSelectors/siteSelectors.ts';
import { setPerSf } from '../../../redux/features/thunks/setPerSf.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { RootState } from '../../../redux/types.ts';
import { useAlerts } from '../../referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { schPrice } from '../types.ts';

// Alert types related to site value/price per SF
const SITE_VALUE_ALERT_TYPES: AlertType[] = [
  'negativeSiteValue',
  'bracketedSiteToValueRatio',
  'limitedSiteToValueRatioRange',
  'largestSiteHasHighestValue',
  'smallestSiteHasLowestValue',
  'inconsistentCompSiteAdjustment',
];

type SummarySectionProps = {
  id: string;
};

export const SummarySection = ({ id }: SummarySectionProps) => {
  const isSubject = id === 'subject' || id === 'subjectArv';
  const dispatch = useAppDispatch();
  const selectSummaryDataDisplayByIdTyped = selectSummaryDataDisplayById as (
    state: RootState,
    id: string,
  ) => SummaryDataDisplay;
  const { actualAgeDisplay, yearBuiltDisplay, siteSizeDisplay, pricePerSfDisplay } = useAppSelector(
    (state: RootState) => selectSummaryDataDisplayByIdTyped(state, id),
  );
  const alerts = useAlerts();
  const subjectSiteArea = useAppSelector((state: RootState) =>
    selectNumericSiteArea(state, 'subject'),
  );

  // Check if there are active alerts related to site value/price per SF for this property
  const hasSiteValueAlert = useMemo(() => {
    const allAlerts = [...alerts.depreciatedCost, ...alerts.comparable, ...alerts.adjustments];
    const alertPropertyID = id === 'subjectArv' ? 'subject' : id;

    return allAlerts.some(
      (alert) =>
        SITE_VALUE_ALERT_TYPES.includes(alert.type) &&
        alert.status === 'active' &&
        alert.propertyID === alertPropertyID,
    );
  }, [alerts, id]);

  const handlePerSfChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = schPrice.safeParse(event.target.value);
    const parsedValue = value.success
      ? (value.data ?? AloftPrice.zero.toJSON())
      : AloftPrice.zero.toJSON();
    dispatch(setPerSf(parsedValue))
      .unwrap()
      .catch(() => undefined);
  };

  return (
    <ContentBlock>
      <Flex flexDirection="row" width="100%">
        <Flex
          flexDirection="column"
          gap="0.25rem"
          flex={1}
          paddingRight="1rem"
          borderRightWidth="1px"
          borderColor="strokeDefault"
          borderStyle="solid"
          alignItems="center"
        >
          <Typography
            variant="body2"
            color="textTertiary"
            __dangerDoNotUseSx={{ whiteSpace: 'nowrap' }}
          >
            Actual Age
          </Typography>
          <Typography
            variant="body1"
            color="textTertiary"
            __dangerDoNotUseSx={{ fontWeight: 'bold' }}
          >
            {actualAgeDisplay}
          </Typography>
        </Flex>

        <Flex
          flexDirection="column"
          gap="0.25rem"
          flex={1}
          paddingX="1rem"
          borderRightWidth="1px"
          borderColor="strokeDefault"
          borderStyle="solid"
          alignItems="center"
        >
          <Typography
            variant="body2"
            color="textTertiary"
            __dangerDoNotUseSx={{ whiteSpace: 'nowrap' }}
          >
            Year Built
          </Typography>
          <Typography
            variant="body1"
            color="textTertiary"
            __dangerDoNotUseSx={{ fontWeight: 'bold' }}
          >
            {yearBuiltDisplay}
          </Typography>
        </Flex>

        <Flex
          flexDirection="column"
          gap="0.25rem"
          flex={1}
          paddingX="1rem"
          borderRightWidth="1px"
          borderColor="strokeDefault"
          borderStyle="solid"
          alignItems="center"
        >
          <Typography
            variant="body2"
            color="textTertiary"
            __dangerDoNotUseSx={{ whiteSpace: 'nowrap' }}
          >
            Site Size
          </Typography>
          <Typography
            variant="body1"
            color="textTertiary"
            __dangerDoNotUseSx={{ fontWeight: 'bold' }}
          >
            {siteSizeDisplay}
          </Typography>
        </Flex>

        <Flex flexDirection="column" gap="0.25rem" flex={1} paddingLeft="1rem" alignItems="center">
          <Typography
            variant="body2"
            color="textTertiary"
            __dangerDoNotUseSx={{ whiteSpace: 'nowrap' }}
          >
            Price per SF
          </Typography>
          {isSubject ? (
            <Block
              className="price-per-sf-input"
              paddingX="0.5rem"
              paddingY="0.25rem"
              borderWidth="1px"
              borderColor="strokeLight"
              borderStyle="solid"
              borderRadius="4px"
              backgroundColor="surface1"
              width="fit-content"
            >
              <Flex flexDirection="row" alignItems="center" gap="0.5rem" flexWrap="nowrap">
                <SimpleInput
                  id={`price-per-sf-${id}`}
                  type="number"
                  value={pricePerSfDisplay}
                  onChange={handlePerSfChange}
                  precision={0}
                  size="xs"
                  variant="grid"
                  useSeparator
                  width="5rem"
                  disabled={!isDefined(subjectSiteArea)}
                  leftAddon={
                    <Typography variant="body1" color="textDisabled">
                      $
                    </Typography>
                  }
                  rightAddon={
                    <Typography variant="body1" color="textDisabled">
                      /sf
                    </Typography>
                  }
                />
                {hasSiteValueAlert ? (
                  <Block
                    title="There is an active alert related to site value or price per square foot."
                    width="20px"
                    height="20px"
                    __dangerDoNotUseSx={{
                      backgroundColor: theme.colors.alertWarning,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon icon="warning" color="textOffset" size="md" />
                  </Block>
                ) : (
                  <Block
                    title="Price per square foot is valid."
                    width="20px"
                    height="20px"
                    __dangerDoNotUseSx={{
                      backgroundColor: theme.colors.alertSuccess,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon icon="check" color="textOffset" size="sm" />
                  </Block>
                )}
              </Flex>
            </Block>
          ) : (
            <Flex flexDirection="row" alignItems="center" gap="0.5rem" flexWrap="nowrap">
              <Typography
                variant="body1"
                color="textTertiary"
                __dangerDoNotUseSx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
              >
                {pricePerSfDisplay}
              </Typography>
            </Flex>
          )}
        </Flex>
      </Flex>
    </ContentBlock>
  );
};
